import React, { useState, useMemo, useEffect } from "react";
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Box,
    TablePagination, Chip, CircularProgress,
    Tooltip, Grid, TextField, InputAdornment, Button,
} from "@mui/material";
import * as XLSX from "xlsx";
import { getClientTheme } from "../../theme/clientTheme";
import { useData } from "../../context/DataContext";
import SingleSelect from "../../components/Selections/singleSelect";
import MultiSelectWithAll from "../../components/Selections/multiSelectWithAll";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const LevelWiseTrainingSessionReport = ({ user_client_id, levelId, trainingTypeId }) => {

    const clientTheme = getClientTheme(user_client_id);
    const { trainingPlanLevelReports = [], loading, user = {}, handleTrainingPlanReport } = useData();

    /* ================= LEVEL DROPDOWN (only user_level_id 1 or 2) ================= */
    const levelOptions = [
        { value: 1, label: "Villages" },
        { value: 2, label: "Groups" },
    ];
    const isTopUser = user.user_level_id === 1 || user.user_level_id === 2;

    const [selectedLevel, setSelectedLevel] = useState(levelId || 1);

    // keep in sync with the Dashboard's level filter
    useEffect(() => {
        if (levelId) setSelectedLevel(levelId);
    }, [levelId]);

    /* ================= NAME FILTER (multi select) + SEARCH ================= */
    const [selectedNames, setSelectedNames] = useState([]);
    const [search, setSearch] = useState("");

    const filterWiseTrainigPlanSummary = useMemo(() => {
        return trainingPlanLevelReports.filter(
            (item) => Number(item.training_type_id) === Number(trainingTypeId)
        );
    }, [trainingPlanLevelReports, trainingTypeId]);

    /* ================= NORMALIZE DATA ================= */
    const normalizedRows = useMemo(() => {
        return filterWiseTrainigPlanSummary.map(row => ({
            ...row,
            trainings: row.trainings?.map((t, i) => ({
                plan_id: t.plan_id ?? i + 1,
                ...t
            })) || []
        }));
    }, [filterWiseTrainigPlanSummary]);
 
    /* ================= UNIQUE TRAINING PLANS ================= */
    const trainingPlans = useMemo(() => {
        return [
            ...new Map(
                normalizedRows
                    .flatMap(r => r.trainings)
                    .map(t => [t.plan_id, t])
            ).values()
        ];
    }, [normalizedRows]);

    /* ================= NAME OPTIONS (village / group) ================= */
    const nameOptions = useMemo(
        () => [
            ...new Map(
                normalizedRows.map(r => [r.village, { value: r.village, label: r.village }])
            ).values()
        ],
        [normalizedRows]
    );

    /* ================= PAGINATION ================= */
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // default: all villages/groups selected (re-applied when the option set changes)
    useEffect(() => {
        setSelectedNames(nameOptions);
    }, [nameOptions]);

    /* ================= FILTER (selected names + search) ================= */
    const filteredRows = useMemo(() => {
        let rows = normalizedRows;

        if (selectedNames.length) {
            const picked = new Set(selectedNames.map(n => n.value));
            rows = rows.filter(r => picked.has(r.village));
        }

        const q = search.trim().toLowerCase();
        if (q) rows = rows.filter(r => (r.village || "").toLowerCase().includes(q));

        return rows;
    }, [normalizedRows, selectedNames, search]);

    const displayedRows = filteredRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    /* ================= EXCEL DOWNLOAD (same grouped tabular format) ================= */
    const handleDownloadExcel = () => {
        const dimLabel = selectedLevel === 1 ? "Village" : "Group";
        const totalCols = 3 + trainingPlans.length * 3; // S.No + dim + Total Farmers + (Status/Present/Absent) per plan

        const row1 = new Array(totalCols).fill("");
        const row2 = new Array(totalCols).fill("");
        const merges = [];

        // fixed columns span both header rows
        ["S.No", dimLabel, "Total Farmers"].forEach((label, c) => {
            row1[c] = label;
            merges.push({ s: { r: 0, c }, e: { r: 1, c } });
        });

        // each training plan = group header across Status / Present / Absent
        let col = 3;
        trainingPlans.forEach((plan) => {
            row1[col] = plan?.name || "-";
            merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + 2 } });
            row2[col] = "Status";
            row2[col + 1] = "Present";
            row2[col + 2] = "Absent";
            col += 3;
        });

        // data rows (current filter set)
        const dataRows = filteredRows.map((row, idx) => {
            const cells = [idx + 1, row.village, row.totalFarmers];
            trainingPlans.forEach((plan) => {
                const training = row.trainings.find((t) => t.plan_id === plan.plan_id);
                cells.push(
                    training?.status || "-",
                    training?.present ?? "-",
                    training?.absent ?? "-"
                );
            });
            return cells;
        });

        const aoa = [row1, row2, ...dataRows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws["!merges"] = merges;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Training Report");
        XLSX.writeFile(wb, `${dimLabel}_Wise_Training_Session_Report.xlsx`);
    };

    /* ================= STATUS STYLING ================= */
    const getStatusChip = (status) => {
        switch (status?.toLowerCase()) {
            case "completed":
                return (
                    <Chip
                        icon={<CheckCircleIcon />}
                        label="Completed"
                        color="success"
                        size="small"
                        variant="filled"
                    />
                );
            case "inprocess":
                return (
                    <Chip
                        icon={<AutorenewIcon />}
                        label="In Process"
                        size="small"
                        sx={{
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            fontWeight: 600,
                            "& .MuiChip-icon": {
                                color: "#fff",
                            },
                        }}
                    />
                );
            case "created":
                return (
                    <Chip
                        icon={<AddCircleIcon />}
                        label="Created"
                        color="info"
                        size="small"
                        variant="filled"
                    />
                );
            default:
                return (
                    <Chip
                        label={status || "-"}
                        size="small"
                        variant="outlined"
                    />
                );
        }
    };

    /* ================= STYLES ================= */
    const headerStyle = {
        border: "1px solid #fff",
        fontWeight: 700,
        backgroundColor: clientTheme.activeBg,
        color: "#FFF",
        textTransform: "uppercase",
        fontSize: "13px",
    };

    const groupHeaderStyle = {
        border: "1px solid #fff",
        fontWeight: 700,
        backgroundColor: clientTheme.activeBg,
        color: "#FFF",
        letterSpacing: "0.5px",
    };

    const subHeaderStyle = {
        border: "1px solid #fff",
        fontWeight: 600,
        backgroundColor: clientTheme.activeBg,
        color: "#FFF",
    };

    const bodyStyle = {
        border: "1px solid #e0e0e0",
        fontSize: "13px",
    };

    if (trainingPlanLevelReports.length === 0) {
        return <Box sx={{ textAlign: "center", alignContent: "center" }}>Training plans and training sessions have not been conducted.</Box>
    }

    if (loading === true) {
        return <CircularProgress color="secondary" size={45}/>
    }

    return (
        <Box sx={{ width: "100%" }}>
            {/* ================= TOOLBAR: LEVEL + NAMES + SEARCH ================= */}
            <Grid container spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                {/* Level dropdown: only for user_level_id 1 or 2 */}
                {isTopUser && (
                    <Grid item size={{ xs: 12, md: 3 }}>
                        <SingleSelect
                            name="selectedLevel"
                            label="Level"
                            options={levelOptions}
                            value={selectedLevel}
                            onChange={(_, value) => {
                                const lvl = +value || 1;
                                setSelectedLevel(lvl);
                                setPage(0);
                                handleTrainingPlanReport?.(lvl);
                            }}
                        />
                    </Grid>
                )}

                <Grid item size={{ xs: 12, md: isTopUser ? 4 : 5 }}>
                    <MultiSelectWithAll
                        name="selectedNames"
                        label={selectedLevel === 1 ? "Select Villages" : "Select Groups"}
                        options={nameOptions}
                        value={selectedNames}
                        onChange={(_, values) => {
                            setSelectedNames(values || []);
                            setPage(0);
                        }}
                    />
                </Grid>

                {/* Search bar on the right */}
                <Grid item size={{ xs: 12, md: isTopUser ? 3 : 4 }} sx={{ ml: "auto" }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={`Search ${selectedLevel === 1 ? "village" : "group"}...`}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Excel download */}
                <Grid item size={{ xs: 12, md: 2 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={handleDownloadExcel}
                        disabled={filteredRows.length === 0}
                    >
                        Excel
                    </Button>
                </Grid>
            </Grid>

            <TableContainer
                component={Paper}
                sx={{
                    overflowX: "auto",
                    borderRadius: 3,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        minWidth: 1000,
                        "& .MuiTableCell-root": {
                            padding: "10px",
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell rowSpan={2} align="center" sx={headerStyle}>
                                S.No
                            </TableCell>
                            <TableCell rowSpan={2} align="center" sx={headerStyle}>
                                {selectedLevel === 1 ? "Village" : "Group"}
                            </TableCell>
                            <TableCell rowSpan={2} align="center" sx={headerStyle}>
                                Total Farmers
                            </TableCell>

                            {trainingPlans.map(plan => (
                                <TableCell
                                    key={plan.plan_id}
                                    colSpan={3}
                                    align="center"
                                    sx={groupHeaderStyle}
                                >
                                    <Tooltip title={plan?.name || ""}>
                                        <span>
                                            {plan?.name?.length > 50
                                                ? `${plan.name.substring(0, 50)}...`
                                                : plan?.name}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            ))}
                        </TableRow>

                        <TableRow
                            hover
                            sx={{
                                transition: "all 0.2s ease",
                                "&:nth-of-type(even)": {
                                    backgroundColor: "#f9fbff",
                                },
                                "&:hover": {
                                    backgroundColor: "#e3f2fd",
                                    transform: "scale(1.002)",
                                },
                            }}
                        >
                            {trainingPlans.map(plan => (
                                <React.Fragment key={plan.plan_id}>
                                    <TableCell align="center" sx={subHeaderStyle}>
                                        Status
                                    </TableCell>
                                    <TableCell align="center" sx={subHeaderStyle}>
                                        Present
                                    </TableCell>
                                    <TableCell align="center" sx={subHeaderStyle}>
                                        Absent
                                    </TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedRows.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={3 + trainingPlans.length * 3}
                                    align="center"
                                    sx={{ ...bodyStyle, py: 3, color: "text.secondary" }}
                                >
                                    No matching {selectedLevel === 1 ? "villages" : "groups"} found
                                </TableCell>
                            </TableRow>
                        )}
                        {displayedRows.map((row, idx) => (
                            <TableRow
                                key={row.id}
                                hover
                                sx={{
                                    "&:nth-of-type(even)": {
                                        backgroundColor: "#fafafa"
                                    }
                                }}
                            >
                                <TableCell align="center" sx={bodyStyle}>
                                    {page * rowsPerPage + idx + 1}
                                </TableCell>

                                <TableCell align="center" sx={bodyStyle}>
                                    {row.village}
                                </TableCell>

                                <TableCell align="center" sx={bodyStyle}>
                                    {row.totalFarmers}
                                </TableCell>

                                {trainingPlans.map(plan => {
                                    const training = row.trainings.find(
                                        t => t.plan_id === plan.plan_id
                                    );

                                    return (
                                        <React.Fragment key={plan.plan_id}>
                                            <TableCell align="center" sx={bodyStyle}>
                                                {getStatusChip(training?.status)}
                                            </TableCell>

                                            <TableCell align="center" sx={bodyStyle}>
                                                {training?.present ?? "-"}
                                            </TableCell>

                                            <TableCell align="center" sx={bodyStyle}>
                                                {training?.absent ?? "-"}
                                            </TableCell>
                                        </React.Fragment>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
            />
        </Box>
    );
};

export default LevelWiseTrainingSessionReport;